import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import axios from "axios";
import { normalizeApiError } from "../api/normalizeApiError";
import { useError } from "../contexts/ErrorContext";
import type { InitialTenantSetupRequest } from "../models/InitialTenantSetupRequest";
import type { Tenant } from "../models/Tenant";
import type { TenantThemeSettings } from "../models/TenantThemeSettings";
import { TenantService } from "../services/TenantService";
import { DefaultTenantTheme } from "../theme/PantryStackTheme";
import { GetTenantUrl } from "../utils/GetTenantUrl";
import {
  TenantContext,
  type ITenantContext,
  type TenantResolutionStatus,
} from "./TenantContext";

interface TenantProviderProps {
  children: ReactNode;
}

export default function TenantProvider({ children }: TenantProviderProps) {
  const tenantService = useMemo(() => TenantService(), []);
  const TenantUrl = useMemo(GetTenantUrl, []);
  const [Tenant, SetTenant] = useState<Tenant>();
  const [Theme, SetTheme] = useState<TenantThemeSettings>(DefaultTenantTheme);
  const [Status, SetStatus] = useState<TenantResolutionStatus>("Initializing");
  const { showError } = useError();

  const ResolveTenant = useCallback(async (): Promise<void> => {
    SetStatus("Initializing");

    try {
      const resolvedTenant = await tenantService.ResolveByUrl(TenantUrl);

      if (resolvedTenant.Data.length == 0) {
        SetTenant(undefined);
        SetTheme(DefaultTenantTheme);
        SetStatus("NotFound");
        return;
      }

      SetTenant(resolvedTenant.Data[0]);
      SetTheme(resolvedTenant.Data[0].Theme ?? DefaultTenantTheme);
      SetStatus("Found");
    } catch (error: unknown) {
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : typeof error === "object" && error !== null && "status" in error
          ? error.status
          : undefined;

      if (status === 404) {
        SetTenant(undefined);
        SetTheme(DefaultTenantTheme);
        SetStatus("NotFound");
        return;
      }

      SetStatus("Error");
      showError(normalizeApiError(error));
    }
  }, [TenantUrl, showError]);

  const PreviewTheme = useCallback((theme: TenantThemeSettings): void => {
    SetTheme(theme);
  }, []);

  const SaveTenant = useCallback(async (request: Tenant): Promise<Tenant> => {
    const savedTenant = await tenantService.SaveTenant(request);
    SetTenant(savedTenant);
    SetTheme(savedTenant.Theme ?? DefaultTenantTheme);
    SetStatus("Found");
    return savedTenant;
  }, []);

  useEffect(() => {
    void ResolveTenant();
  }, [ResolveTenant]);

  const value = useMemo<ITenantContext>(
    () => ({
      Tenant,
      TenantUrl,
      Theme,
      Status,
      ResolveTenant,
      PreviewTheme,
      SaveTenant,
    }),
    [
      Tenant,
      TenantUrl,
      Theme,
      Status,
      ResolveTenant,
      PreviewTheme,
      SaveTenant,
    ],
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}
